import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Scale factor for the entire login content (1.0 = 100%, 0.75 = 75%)
  const [scaleFactor] = useState(0.8);
  
  // Set CSS variable for the scale factor to use in media queries
  useEffect(() => {
    document.documentElement.style.setProperty('--scale-factor', scaleFactor.toString());
  }, [scaleFactor]);

  // SVG asset paths
  const loginLeftSvg = `${process.env['PUBLIC_URL'] || ''}/images/login-left.svg`;
  const loginRightSvg = `${process.env['PUBLIC_URL'] || ''}/images/login-right.svg`;
  const loginSignInSvg = `${process.env['PUBLIC_URL'] || ''}/images/login-signin.svg`;
  const flarionLogoSvg = `${process.env['PUBLIC_URL'] || ''}/images/flarion-black-white.svg`;
  const loginRocketImageSvg = `${process.env['PUBLIC_URL'] || ''}/images/login-rocket-image.svg`;

  useEffect(() => {
    // Authentication flow utility functions
    const tokenUtils = {
      // Get cookie by name
      getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
      },

      // Parse JWT token payload
      parseJwt(token: string): { exp?: number } | null {
        try {
          const base64Url = token.split('.')[1];
          if (!base64Url) return null;
          return JSON.parse(atob(base64Url));
        } catch (e) {
          return null;
        }
      },

      // Check if token is valid and not expired
      isTokenValid(token: string | null): boolean {
        if (!token) return false;
        const payload = this.parseJwt(token);
        return Boolean(payload && payload.exp && payload.exp * 1000 > Date.now());
      },

      // Log token information for debugging
      logTokenInfo(token: string | null, tokenType: string): void {
        if (!token) {
          console.log(`No ${tokenType} token found`);
          return;
        }

        const payload = this.parseJwt(token);
        if (!payload) {
          console.log(`Invalid ${tokenType} token format`);
          return;
        }

        if (payload.exp === undefined) {
          console.log(`${tokenType} token missing expiration`);
          return;
        }

        const expiryTime = new Date(payload.exp * 1000);
        const isValid = payload.exp * 1000 > Date.now();
        console.log(`${tokenType} token expiry:`, expiryTime);
        console.log(`${tokenType} token is ${isValid ? 'valid' : 'expired'}`);
      },

      // Set access token cookie
      setAccessToken(token: string): void {
        const expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + (1 * 60 * 60 * 1000)); // 1 hour
        document.cookie = `access_token=${token}; expires=${expiresDate.toUTCString()}; path=/;`;
        console.log('Set new access token cookie');
      }
    };

    const validateAuth = async () => {
      try {
        // STEP 1: Check access token
        const accessToken = tokenUtils.getCookie('access_token');
        tokenUtils.logTokenInfo(accessToken, 'Access');

        // If access token is valid, redirect to overview
        if (tokenUtils.isTokenValid(accessToken)) {
          console.log('Access token is valid, redirecting to overview');
          navigate('/overview');
          return;
        }

        console.log('Access token invalid or expired, trying refresh token');

        // STEP 2: Try refresh token if access token is invalid
        const refreshToken = tokenUtils.getCookie('refresh_token');
        tokenUtils.logTokenInfo(refreshToken, 'Refresh');

        // If refresh token is missing or invalid, stay on login page
        if (!tokenUtils.isTokenValid(refreshToken)) {
          console.log('Refresh token invalid or missing, user must log in');
          return;
        }

        // STEP 3: Use refresh token to get a new access token
        console.log('Refresh token valid, attempting to refresh access token');
        try {
          const response = await fetch('https://api.flarion.io/user/v1.0/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          });

          if (!response.ok) {
            console.log(`Token refresh failed with status: ${response.status}`);
            return;
          }

          // STEP 4: Handle refresh response
          console.log('Token refresh request successful');
          const data = await response.json();

          if (data && data.access_token) {
            // Set new access token in cookie
            tokenUtils.setAccessToken(data.access_token);
            console.log('Successfully set new access token from refresh response');

            // STEP 5: Validate the new token
            const newAccessToken = tokenUtils.getCookie('access_token') || data.access_token;
            tokenUtils.logTokenInfo(newAccessToken, 'New access');

            if (tokenUtils.isTokenValid(newAccessToken)) {
              console.log('New access token valid after refresh, redirecting to overview');
              navigate('/overview');
              return;
            } else {
              console.log('New access token invalid after refresh, authentication failed');
            }
          } else {
            // Check if server set cookies directly instead of in response body
            console.log('No access token in refresh response, checking for server-set cookies');
            const serverToken = tokenUtils.getCookie('access_token');
            
            if (tokenUtils.isTokenValid(serverToken)) {
              console.log('Server-set access token valid, redirecting to overview');
              navigate('/overview');
              return;
            } else {
              console.log('No valid access token available after refresh');
            }
          }
        } catch (error) {
          console.error('Error during refresh token process:', error);
        }
      } catch (error) {
        console.error('Authentication validation error:', error);
      }
    };

    validateAuth();
  }, [navigate]);

  const handleLogin = () => {
    window.location.href = 'https://api.flarion.io/user/v1.0/login/google';
  };

  return (
    <div 
      className={`w-full h-screen flex justify-center items-center relative overflow-hidden ${
        isDark ? 'bg-gray-900' : 'bg-gray-100'
      }`}
    >
      {/* Combined SVG container with overlapping left and right SVGs */}
      <div className="absolute inset-0 flex justify-center items-center">
        <div className="relative h-full" style={{ width: '100%', maxWidth: `${1528 * scaleFactor}px` }}>
          {/* Centered container with proper overlap */}
          <div className="relative h-full flex justify-center" style={{ width: '100%' }}>
            {/* Left SVG with sign-in button - visible on all screen sizes */}
            <div className="relative h-full" style={{ width: `${809 * scaleFactor}px`, zIndex: 5, maxWidth: '100%' }}>
              <div className="relative h-full">
                <img 
                  src={loginLeftSvg} 
                  alt="Left decorative element" 
                  className="h-full"
                  style={{ 
                    objectFit: 'contain',
                    marginRight: '-5%', // Negative margin to create overlap
                    width: '100%'
                  }}
                />
              </div>
              
              {/* Content container - vertically centered */}
              <div 
                style={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: `${600 * scaleFactor}px`,
                  maxWidth: '95%',
                  margin: '0 auto'
                }}
              >
                {/* Flarion logo */}
                <div style={{ marginBottom: `${30 * scaleFactor}px` }}>
                  <img 
                    src={flarionLogoSvg} 
                    alt="Flarion Logo" 
                    style={{ 
                      width: `${180 * scaleFactor}px`,
                      height: 'auto'
                    }}
                  />
                </div>
                
                {/* Headline text */}
                <div
                  className="headline-text"
                  style={{ 
                    color: 'var(--Flarion-type, #1E1E1E)',
                    textAlign: 'center',
                    fontFamily: '"ID Grotesk", sans-serif',
                    fontSize: `${57.538 * scaleFactor}px`,
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: `${64.251 * scaleFactor}px`,
                    letterSpacing: '-2.302px',
                    marginBottom: `${40 * scaleFactor}px`,
                    marginLeft: '20px',
                    marginRight: '20px'
                  }}
                >
                  Faster, smarter, more powerful data processing
                </div>
                
                {/* Text directly above sign-in button */}
                <div
                  style={{ 
                    color: 'var(--Flarion-type, #1E1E1E)',
                    textAlign: 'center',
                    fontFamily: '"ID Grotesk", sans-serif',
                    fontSize: `${23.015 * scaleFactor}px`,
                    fontStyle: 'normal',
                    fontWeight: 300,
                    lineHeight: 'normal',
                    marginBottom: `${20 * scaleFactor}px`
                  }}
                >
                  Just plug &amp; play — zero code changes.
                </div>
                
                {/* Sign In button */}
                <button 
                  onClick={handleLogin}
                  className="border-none bg-transparent cursor-pointer transition-transform hover:scale-105 focus:outline-none flex justify-center w-full"
                >
                  <img 
                    src={loginSignInSvg} 
                    alt="Sign In" 
                    className="h-auto"
                    style={{ width: `${110 * scaleFactor}%` }}
                  />
                </button>
              </div>
            </div>
            
            {/* Right SVG - with negative margin to create overlap - hidden on mobile */}
            <div className="relative h-full hidden md:block" style={{ width: `${719 * scaleFactor}px`, zIndex: 10 }}>
              <div className="relative h-full">
                <img 
                  src={loginRightSvg} 
                  alt="Right decorative element" 
                  className="h-full"
                  style={{ 
                    objectFit: 'contain',
                    marginLeft: '-5%' // Negative margin to create overlap
                  }}
                />
                
                {/* Rocket image positioned in center of the right SVG */}
                <img 
                  src={loginRocketImageSvg}
                  alt="Rocket illustration"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '47.5%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Terms of Use text - fixed at the bottom */}
      <div 
        className="fixed bottom-4 left-0 right-0 text-center z-30"
        style={{ 
          fontFamily: '"ID Grotesk", sans-serif',
          fontSize: `${20 * scaleFactor}px`
        }}
      >
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          By logging in, you acknowledge that you have read and agree to the{' '}
          <a 
            href="https://flarion-public.s3.us-west-2.amazonaws.com/20250511-FlarionTermsOfUse.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={`underline ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-black'}`}
          >
            Flarion Terms of Use
          </a>.
        </p>
      </div>
    </div>
  );
};

export default Login;